import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { aws } from '../lib/awsdb';
import InvoiceTracker from '../invoices/InvoiceTracker';
import PoTracker from '../po/PoTracker';
import Subscriptions from './Subscriptions';
import StaffCost from './StaffCost';
import FinanceDashboard from './FinanceDashboard';
import EomReport from './eom/EomReport';

const TAB_STORAGE_KEY = 'fon-finance-active-tab';
// 'staffcost' holds salary data — OWNER-only (Mo). It's in VALID_TABS so the
// owner's selection persists, but visibility + render are gated on the
// am_i_owner() RPC below (fail closed) so nobody else can land on it.
const VALID_TABS = new Set(['dashboard', 'invoices', 'potracker', 'subscriptions', 'staffcost', 'eom']);

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

export default function Finance({ session }) {
  // Owner gate for the Staff Cost tab. null = unknown (RPC in flight) —
  // fail closed: only an explicit `true` reveals the tab. Backed by the
  // SECURITY DEFINER am_i_owner() (migration 154), which returns a boolean.
  const [isOwner, setIsOwner] = useState(null);
  useEffect(() => {
    let alive = true;
    aws.rpc('am_i_owner')
      .then(({ data }) => { if (alive) setIsOwner(data === true); })
      .catch(() => { if (alive) setIsOwner(false); });
    return () => { alive = false; };
  }, []);

  // Persist active tab. Validate against VALID_TABS so a stale value falls
  // back to dashboard cleanly.
  const [tab, setTab] = useState(() => {
    try {
      const stored = localStorage.getItem(TAB_STORAGE_KEY);
      if (stored && VALID_TABS.has(stored)) return stored;
    } catch {}
    return 'dashboard';
  });
  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch {}
  }, [tab]);
  // If the owner check resolves false while sitting on staffcost (e.g. a
  // non-owner restored it from storage), bounce to dashboard.
  useEffect(() => {
    if (isOwner === false && tab === 'staffcost') setTab('dashboard');
  }, [isOwner, tab]);

  const [catalog, setCatalog] = useState([]);
  const [counts, setCounts] = useState({ invoices: null, potracker: null, subscriptions: null });

  const loadCatalog = useCallback(async () => { const { data } = await aws.from('forecaster_catalog').select('*').neq('status', 'retired').order('sku_name'); setCatalog(data || []); }, []);
  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Tab counts — open invoices, open POs, active subscriptions. Cheap
  // count(*) queries against PostgREST via prefer=count=exact + head=true.
  // Failures are silent so a single broken count never blocks the UI.
  useEffect(() => {
    let alive = true;
    async function loadCounts() {
      try {
        const [openInv, openPo, activeSub] = await Promise.all([
          aws.from('po_invoices').select('id', { count: 'exact', head: true }).is('archived_at', null).neq('status', 'paid'),
          aws.from('purchase_orders').select('id', { count: 'exact', head: true }).is('archived_at', null).neq('status', 'received'),
          aws.from('subscriptions').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        ]);
        if (!alive) return;
        // postgrest-js returns count from the Content-Range header. If the
        // header is malformed or '*/*' (count unknown — happens on some
        // edge / WAF states), parseInt yields NaN. `?? null` doesn't catch
        // NaN because NaN is type 'number'. Number.isFinite excludes NaN
        // and Infinity, leaving real integers only.
        const safe = (c) => (Number.isFinite(c) ? c : null);
        setCounts({
          invoices: safe(openInv?.count),
          potracker: safe(openPo?.count),
          subscriptions: safe(activeSub?.count),
        });
      } catch (err) {
        // soft-fail — tabs still render without counts
      }
    }
    loadCounts();
    return () => { alive = false; };
  }, []);

  const today = new Date();
  const fxAsOf = today.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });

  return (
    <div className="app-screen" data-screen-label="Finance">
      <FinanceHeader tab={tab} setTab={setTab} counts={counts} fxAsOf={fxAsOf} isOwner={isOwner === true} />
      <div className="main-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
          >
            {tab === 'dashboard' && <FinanceDashboard session={session} onNav={setTab} />}
            {tab === 'invoices' && <InvoiceTracker session={session} embedded catalog={catalog} />}
            {tab === 'potracker' && <PoTracker session={session} embedded />}
            {tab === 'subscriptions' && <Subscriptions session={session} />}
            {tab === 'eom' && <EomReport />}
            {tab === 'staffcost' && isOwner === true && <StaffCost session={session} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Header — title + meta + tab strip with counts ──────────────────────
// Inline styles for the same reason as the Stock header: avoid touching
// app.css and keep the legacy `fc-subtab` purple-accent rule for Admin.
function FinanceHeader({ tab, setTab, counts, fxAsOf, isOwner }) {
  const tabs = [
    { id: 'dashboard',     label: 'Dashboard' },
    { id: 'invoices',      label: 'Invoices',      count: counts.invoices },
    { id: 'potracker',     label: 'PO Tracker',    count: counts.potracker },
    { id: 'subscriptions', label: 'Subscriptions', count: counts.subscriptions },
    // Financial Report — import the month's Xero financial export, generate the
    // standardised management report, export to Excel. No count badge: nothing
    // is stored yet, each run produces a report on the spot.
    { id: 'eom',           label: 'Financial Report' },
    // Staff Cost — salary data, OWNER-only. Sits directly after Subscriptions.
    ...(isOwner ? [{ id: 'staffcost', label: 'Staff Cost' }] : []),
  ];

  return (
    <div style={{ padding: '28px 36px 0', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 20 }}>
        <div>
          <h1 style={{
            fontFamily: SERIF, fontSize: 30, letterSpacing: '-0.015em',
            color: '#0F1419', margin: 0, fontWeight: 400,
          }}>Finance</h1>
          <p style={{ fontSize: 13, color: '#5C6166', margin: '2px 0 0' }}>
            Cash flow, commitments, and spend at a glance
          </p>
        </div>
        <div style={{
          fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#8B9094',
        }}>
          All amounts in AUD · FX as of {fxAsOf}
        </div>
      </div>
      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid #EFEDE7', marginTop: 4,
      }}>
        {tabs.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '10px 16px 12px', marginBottom: -1,
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                color: active ? '#0F1419' : '#5C6166',
                borderBottom: active ? '2px solid #036E43' : '2px solid transparent',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {t.label}
              {Number.isFinite(t.count) && (
                <span style={{
                  padding: '1px 6px', borderRadius: 10,
                  fontFamily: MONO, fontSize: 10, fontWeight: 500,
                  background: active ? '#ECF6F0' : '#EFEDE7',
                  color: active ? '#036E43' : '#5C6166',
                  lineHeight: 1.6,
                }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
